import AppObj from 'model/appConfigObj';
import { TaskInitParams } from 'model/taskObj';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import pdfViewerApi from './api';
import { desktopAppTaskContex } from 'screen/desktop';
import { fileBasename, isPdfFile } from 'util/openableFile';
import './pdfViewer.css';

export const pdfViewerConfig = {
  appID: 'pdfViewer',
  showName: 'PDF Viewer',
  onList: false,
  icon: require('./images/pdfViewer.png'),
  maxHead: 8,
  defaultFullScreen: true,
  req: {
    minSize: { width: 420, height: 320 },
    defSize: { width: 760, height: 560 },
  },
};

function parseInitPath(parms?: TaskInitParams[]) {
  let pdfPath = '';
  if (parms) {
    for (const p of parms) {
      if (p.command === 'pdfPath' && p.value) {
        pdfPath = p.value;
        break;
      }
    }
  }
  return pdfPath && isPdfFile(fileBasename(pdfPath)) ? pdfPath : '';
}

function PdfViewer(props: { taskInitParms?: TaskInitParams[]; taskID?: string }) {
  const [desktopControll] = useContext(desktopAppTaskContex);
  const currentPath = useMemo(() => parseInitPath(props.taskInitParms), [props.taskInitParms]);
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!props.taskID || !currentPath) {
      return;
    }
    desktopControll?.setTaskTitle?.(props.taskID, fileBasename(currentPath));
  }, [props.taskID, currentPath, desktopControll]);

  useEffect(() => {
    if (!currentPath) {
      setSrc(null);
      return;
    }
    let cancelled = false;
    let ticket = '';
    let renewTimer = 0;
    setLoading(true);
    setError(null);
    setSrc(null);

    const attach = (nextTicket: string, expiresInMs: number) => {
      ticket = nextTicket;
      setSrc(pdfViewerApi.filesStreamUrl(nextTicket));
      if (renewTimer) {
        window.clearTimeout(renewTimer);
      }
      const wait = Math.max(30_000, expiresInMs - 60_000);
      renewTimer = window.setTimeout(() => {
        void pdfViewerApi
          .createMediaTicket(currentPath)
          .then((again) => {
            const next = again.data?.data;
            if (cancelled || !next?.ticket) {
              return;
            }
            const old = ticket;
            attach(next.ticket, next.expiresInMs || wait);
            if (old && old !== next.ticket) {
              void pdfViewerApi.revokeMediaTicket(old);
            }
          })
          .catch(() => {});
      }, wait);
    };

    void pdfViewerApi
      .createMediaTicket(currentPath)
      .then((res) => {
        const data = res.data?.data;
        ticket = data?.ticket || '';
        if (cancelled) {
          if (ticket) void pdfViewerApi.revokeMediaTicket(ticket);
          return;
        }
        if (!res.data?.success || !ticket) {
          throw new Error(res.data?.message || 'Cannot create media ticket');
        }
        attach(ticket, data?.expiresInMs || 30 * 60 * 1000);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || 'Failed to open PDF');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (renewTimer) {
        window.clearTimeout(renewTimer);
      }
      if (ticket) void pdfViewerApi.revokeMediaTicket(ticket);
    };
  }, [currentPath]);

  if (!currentPath) {
    return (
      <div className="pv-root">
        <div className="pv-empty">No PDF to open</div>
      </div>
    );
  }

  return (
    <div className="pv-root">
      <div className="pv-stage">
        {loading && <div className="pv-msg">Loading…</div>}
        {error && !loading && <div className="pv-msg pv-error">{error}</div>}
        {!loading && !error && src && (
          <iframe
            key={src}
            className="pv-frame"
            title={fileBasename(currentPath) || 'PDF'}
            src={src}
          />
        )}
      </div>

      <div className="pv-status">
        <span title={currentPath}>{currentPath}</span>
      </div>
    </div>
  );
}

const App: AppObj = {
  config: pdfViewerConfig,
  body: PdfViewer,
};

export default App;
